'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowUpRight, Building2, Home, Key, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn, BLUR_DATA_URL } from '@/lib/utils';
import { fadeUp, staggerContainer } from '@/lib/motion';

interface BentoTile {
  href: string;
  eyebrow: string;
  title: string;
  description: string;
  icon: React.ElementType;
  image?: string;
  /** Tailwind col/row span classes. */
  span?: string;
  /** Color variant. */
  tone?: 'navy' | 'cream' | 'ochre' | 'image';
}

const DEFAULT_TILES: BentoTile[] = [
  {
    href: '/acheter',
    eyebrow: 'Acheter',
    title: 'Trouver le bien qui vous ressemble',
    description: 'Du studio en médina au riad de caractère.',
    icon: Home,
    image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80',
    span: 'md:col-span-2 md:row-span-2',
    tone: 'image',
  },
  {
    href: '/louer',
    eyebrow: 'Louer',
    title: 'Locations soigneusement sélectionnées',
    description: 'Court & long séjour, partout au Maroc.',
    icon: Key,
    span: 'md:col-span-2',
    tone: 'navy',
  },
  {
    href: '/demande-personnalisee',
    eyebrow: 'Sur mesure',
    title: 'Demande personnalisée',
    description: 'Décrivez le bien idéal, on le trouve pour vous.',
    icon: Building2,
    tone: 'cream',
  },
  {
    href: '/nous-confier-un-bien',
    eyebrow: 'Vendre',
    title: 'Confier votre bien',
    description: 'Estimation gratuite, mise en valeur éditoriale.',
    icon: Users,
    tone: 'ochre',
  },
];

interface BentoGridProps {
  tiles?: BentoTile[];
}

export function BentoGrid({ tiles = DEFAULT_TILES }: BentoGridProps) {
  return (
    <motion.div
      variants={staggerContainer(0.06, 0.05)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '0px 0px -10% 0px' }}
      className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[minmax(220px,1fr)] gap-4"
    >
      {tiles.map((tile, i) => (
        <motion.div
          key={i}
          variants={fadeUp}
          className={cn('h-full', tile.span)}
        >
          <BentoCard tile={tile} />
        </motion.div>
      ))}
    </motion.div>
  );
}

function BentoCard({ tile }: { tile: BentoTile }) {
  const Icon = tile.icon;

  const toneCls = {
    image: 'bg-zinc-900 text-white',
    navy: 'bg-zinc-900 text-white',
    cream: 'bg-surface-cream text-ink',
    ochre: 'bg-accent text-accent-foreground',
  }[tile.tone || 'cream'];

  const eyebrowCls = {
    image: 'text-white/70',
    navy: 'text-white/70',
    cream: 'text-accent',
    ochre: 'text-accent-foreground/85',
  }[tile.tone || 'cream'];

  const descCls = {
    image: 'text-white/70',
    navy: 'text-white/70',
    cream: 'text-ink-secondary',
    ochre: 'text-accent-foreground/85',
  }[tile.tone || 'cream'];

  return (
    <Link
      href={tile.href}
      className={cn(
        'group relative h-full rounded-3xl overflow-hidden p-7 md:p-8 flex flex-col justify-between transition-all duration-400 ease-premium hover:-translate-y-1 hover:shadow-card-hover focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
        toneCls,
      )}
    >
      {tile.tone === 'image' && tile.image && (
        <>
          <Image
            src={tile.image}
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            className="object-cover transition-transform duration-700 ease-premium group-hover:scale-[1.04]"
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-zinc-900/20" />
        </>
      )}

      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <span className={cn('eyebrow', eyebrowCls)}>{tile.eyebrow}</span>
          <span
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-300',
              tile.tone === 'cream'
                ? 'border-ink/15 text-ink group-hover:bg-ink group-hover:text-primary-foreground'
                : 'border-current/30 text-current group-hover:bg-current/15',
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
        </div>
      </div>

      <div className="relative">
        <h3 className="font-display text-h2 leading-tight mb-2">{tile.title}</h3>
        <p className={cn('text-sm leading-relaxed', descCls)}>{tile.description}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium opacity-90 group-hover:opacity-100">
          Découvrir
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}
